import { Core } from "@faire/design-tokens";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web/common/localization";
import { NotificationBell } from "@faire/web/slate/icons/foundation/NotificationBell";
import { NotificationBellChecked } from "@faire/web/slate/icons/foundation/NotificationBellChecked";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { createToast } from "@faire/web/slate/Toast";
import { Span } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { useViewport__deprecated } from "@faire/web/ui/hooks/useViewport";
import React, { useState } from "react";
import styled, { css } from "styled-components";

import { BackInStockToast } from "@faire/retailer/features/BackInStock/BackInStockToast";
import { useEventSubscriptionStore } from "@faire/retailer/stores/domain/EventSubscriptionStore";

import { ButtonBase, Symbol } from "../Button/RoundButtonStyles";

interface ITouchDeviceRoundNotifyMeButtonProps {
  handleNotifyMeClick: () => void;
  componentSize: string;
}

const TouchDeviceRoundNotifyMeButton: React.FC<
  ITouchDeviceRoundNotifyMeButtonProps
> = ({ handleNotifyMeClick, componentSize }) => {
  return (
    <ButtonBackground $isBase={true} $isExpanded={false}>
      <ButtonBase
        data-test-id="touch-round-notify-me-base-button"
        $size={componentSize}
        onClick={(e: { preventDefault: () => void }) => {
          e.preventDefault();
          handleNotifyMeClick();
        }}
      >
        <Symbol data-test-id="base-notify-icon">
          <NotificationBell />
        </Symbol>
      </ButtonBase>
    </ButtonBackground>
  );
};

interface INonTouchRoundNotifyMeButtonProps {
  handleNotifyMeClick: () => void;
  setIsExpanded: (isExpanded: boolean) => void;
  isExpanded: boolean;
  componentSize: string;
}

const NonTouchRoundNotifyMeButton: React.FC<
  INonTouchRoundNotifyMeButtonProps
> = ({ handleNotifyMeClick, setIsExpanded, componentSize, isExpanded }) => {
  const { strictLocalize } = useStrictLocalization();

  if (isExpanded) {
    const notifyMeText = strictLocalize({
      defaultMessage: "Notify me",
      description: {
        text: "Show this when hover over the notify button on the product tile",
        img: "https://cdn.faire.com/fastly/4780092b421c628c1ec4ff52f357c5ce332a23d03c9132c92a4e40f24184894f.png",
      },
    });
    return (
      <ButtonBackground
        data-test-id="round-notify-me-expanded-button"
        $isBase={false}
        $isExpanded={true}
        onClick={(e: { preventDefault: () => void }) => {
          e.preventDefault();
          handleNotifyMeClick();
        }}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <Span variant="paragraphSansMedium" style={{ color: "inherit" }}>
          {notifyMeText}
        </Span>
      </ButtonBackground>
    );
  } else {
    return (
      <ButtonBackground $isBase={true} $isExpanded={false}>
        <ButtonBase
          data-test-id="round-notify-me-base-button"
          $size={componentSize}
          onMouseEnter={() => setIsExpanded(true)}
        >
          <Symbol data-test-id="base-notify-icon">
            <NotificationBell />
          </Symbol>
        </ButtonBase>
      </ButtonBackground>
    );
  }
};

interface IRoundNotifyMeButtonBaseProps {
  handleNotifyMeClick: () => void;
  handleNotifiedClick: () => void;
  setIsExpanded: (isExpanded: boolean) => void;
  isClicked: boolean;
  isExpanded: boolean;
  isSubscriedToBackInStock?: boolean;
  componentSize: string;
}

const RoundNotifyMeButtonBase: React.FC<IRoundNotifyMeButtonBaseProps> = ({
  handleNotifyMeClick,
  handleNotifiedClick,
  setIsExpanded,
  isClicked,
  isSubscriedToBackInStock,
  componentSize,
  isExpanded,
}) => {
  const viewport = useViewport__deprecated();

  if (isClicked || isSubscriedToBackInStock) {
    return (
      <ButtonBackground
        data-test-id="round-notified-button"
        $isBase={false}
        $isExpanded={false}
        onClick={(e: { preventDefault: () => void }) => {
          e.preventDefault();
          handleNotifiedClick();
        }}
      >
        <ButtonBase
          $size={componentSize}
          style={{ "--size": componentSize } as React.CSSProperties}
        >
          <Symbol data-test-id="notified-icon">
            <NotificationBellChecked color={Core.icon.inverse} />
          </Symbol>
        </ButtonBase>
      </ButtonBackground>
    );
  }

  if (viewport.isTouchDevice) {
    return (
      <TouchDeviceRoundNotifyMeButton
        handleNotifyMeClick={handleNotifyMeClick}
        componentSize={componentSize}
      />
    );
  } else {
    return (
      <NonTouchRoundNotifyMeButton
        handleNotifyMeClick={handleNotifyMeClick}
        setIsExpanded={setIsExpanded}
        componentSize={componentSize}
        isExpanded={isExpanded}
      />
    );
  }
};

interface IRoundNotifyMeButtonProps {
  productOptionToken: string;
  isSubscriedToBackInStock?: boolean;
  deleteBackInStockNotification?: (productOptionToken: string) => void;
}

export const RoundNotifyMeButton: React.FC<IRoundNotifyMeButtonProps> = ({
  productOptionToken,
  isSubscriedToBackInStock,
  deleteBackInStockNotification,
}) => {
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const componentSize = getSpacing("4x");

  const debouncedCloseExpandedButton = debounceIfPossible(
    () => setIsExpanded(false),
    3000
  );
  React.useEffect(() => {
    if (isExpanded) {
      debouncedCloseExpandedButton();
    }
  }, [isExpanded, debouncedCloseExpandedButton]);

  const { subscribeBackInStock, unsubscribeBackInStock } =
    useEventSubscriptionStore([
      "subscribeBackInStock",
      "unsubscribeBackInStock",
    ]);
  const handleNotifyMeClick = async () => {
    try {
      await subscribeBackInStock(productOptionToken);
      createToast(BackInStockToast(), { timeout: "short" });
      setIsClicked(true);
    } catch (e) {
      setIsClicked(false);
    } finally {
      setIsExpanded(false);
    }
  };

  const handleNotifiedClick = async () => {
    try {
      if (deleteBackInStockNotification !== undefined) {
        await unsubscribeBackInStock(productOptionToken);
        deleteBackInStockNotification(productOptionToken);
        setIsClicked(false);
      }
    } catch (e) {
      setIsClicked(true);
    }
  };

  return (
    <ClickOutside onClickOutside={() => setIsExpanded(false)}>
      <Column>
        <RoundNotifyMeButtonBase
          handleNotifyMeClick={handleNotifyMeClick}
          handleNotifiedClick={handleNotifiedClick}
          setIsExpanded={setIsExpanded}
          isClicked={isClicked}
          isSubscriedToBackInStock={isSubscriedToBackInStock}
          componentSize={componentSize}
          isExpanded={isExpanded}
        />
      </Column>
    </ClickOutside>
  );
};

const ButtonBackground = styled.div<{
  $isExpanded: boolean;
  $isBase: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: max-content;
  padding-left: ${(props) => (props.$isExpanded ? getSpacing("2x") : "0")};
  padding-right: ${(props) => (props.$isExpanded ? getSpacing("2x") : "0")};
  height: ${getSpacing("4x")};
  transition: all 150ms linear;
  border-radius: 16px;
  ${({ $isBase }) =>
    $isBase
      ? css`
          background-color: ${Core.surface.inverse};
          color: ${Core.text.default};
        `
      : css`
          background-color: ${Core.surface.default};
          color: ${Core.text.inverse};
        `};
`;
